import { GETTING_STARTED_ARTICLES } from './gettingStarted.jsx';
import { DASHBOARD_ARTICLES } from './dashboard.jsx';
import { FINDING_CASES_ARTICLES } from './findingCases.jsx';
import { REPORTING_ARTICLES } from './reporting.jsx';
import { WORKING_CASES_ARTICLES } from './workingCases.jsx';
import { ADMINISTRATION_ARTICLES } from './administration.jsx';
import { CLINIC_ACCESS_ARTICLES } from './clinicAccess.jsx';

/**
 * Knowledge article registry.
 *
 * Articles are content-as-code (see knowledge_logic.md at the repo root):
 * each is { id, title, category, summary, keywords, visibleTo, Body }.
 * `visibleTo(user)` is a predicate on the same capability booleans the app
 * gates its UI with — an article a user can't act on is simply absent from
 * their TOC, never greyed out.
 *
 * Category order is reading order: the whole guide is meant to work
 * front-to-back as an onboarding path.
 */
export const CATEGORIES = [
  { id: 'getting-started', label: 'Getting started' },
  { id: 'dashboard',       label: 'Dashboard & analytics' },
  { id: 'finding-cases',   label: 'Finding & viewing cases' },
  { id: 'reporting',       label: 'Reporting an event' },
  { id: 'working-cases',   label: 'Working a case' },
  { id: 'administration',  label: 'Administration' },
];

const CATEGORY_ORDER = Object.fromEntries(CATEGORIES.map((c, i) => [c.id, i]));

export const ARTICLES = [
  ...GETTING_STARTED_ARTICLES,
  ...DASHBOARD_ARTICLES,
  ...FINDING_CASES_ARTICLES,
  ...REPORTING_ARTICLES,
  ...WORKING_CASES_ARTICLES,
  ...ADMINISTRATION_ARTICLES,
  ...CLINIC_ACCESS_ARTICLES,
].sort((a, b) => (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99));

export function categoryLabel(id) {
  return CATEGORIES.find(c => c.id === id)?.label ?? id;
}

// The articles this user's tier can see, in reading order.
export function visibleArticles(user) {
  return ARTICLES.filter(a => {
    try { return !!a.visibleTo(user); } catch { return false; }
  });
}

// Case-insensitive match on title, summary, and keywords — TOC search.
export function matchesQuery(article, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    article.title.toLowerCase().includes(q) ||
    article.summary.toLowerCase().includes(q) ||
    article.keywords.some(k => k.toLowerCase().includes(q))
  );
}
